"use client";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Flex,
  Text,
  Button,
  Image,
  Grid,
  GridItem,
  Box,
} from "@chakra-ui/react";
import { useEffect } from "react";
import CryptoPaymentModal from "./CryptoPaymentsModal";
import creditCard from "@public/credit_card.svg";
import poweredByStripe from "@public/powered_by_stripe.svg";
import kesspayCard from "@public/kesspay_credit.svg";
import applePayLogo from "@public/apple_pay_logo.svg";
import { useCreateStripeCheckoutSession } from "queries";
import { useCreateKessPayCheckoutSession } from "queries";
import { useCreateQFPayCheckoutSession } from "queries";

interface Props {
  isPaymentsOpen: boolean;
  onClosePayments: () => void;
  price: number;
  plan: string;
  onPaypalCheckout: () => void;
}

const PaymentsModal = ({
  isPaymentsOpen,
  onClosePayments,
  price,
  plan,
  onPaypalCheckout,
}: Props) => {
  const createStripeCheckoutSession = useCreateStripeCheckoutSession();
  const createKeypassCheckoutSession = useCreateKessPayCheckoutSession();
  const createQFPayCheckoutSession = useCreateQFPayCheckoutSession();

  useEffect(() => {
    onPaypalCheckout();
  }, [onPaypalCheckout]);

  const handleClosePayments = () => {
    onClosePayments();
  };

  const handleStripeCheckout = () => {
    createStripeCheckoutSession.mutate(
      { plan_name: plan },
      {
        onSuccess: () => {
          console.log("Created Stripe session successfully");
        },
        onError: (error: any) => {
          console.error("Error creating Stripe session:", error);
        },
      }
    );
  };

  const handleKessPayCheckout = () => {
    createKeypassCheckoutSession.mutate(
      { plan_name: plan },
      {
        onSuccess: (data: { session_id: string; payment_url: string }) => {
          console.log("Created KessPay session successfully", data);
          window.open(data.payment_url, '_blank');
        },
        onError: (error: any) => {
          console.error("Error creating KessPay session:", error);
        },
      }
    );
  };

  const handleQFPayCheckout = () => {
    createQFPayCheckoutSession.mutate(
      { plan_name: plan },
      {
        onSuccess: (data: { session_id: string; payment_url: string }) => {
          console.log("Created QFPay session successfully", data);
          localStorage.setItem("payment_session_id", data.session_id);
          localStorage.setItem("payment_provider", "qfpay");
          window.open(data.payment_url, '_blank');
        },
        onError: (error: any) => {
          console.error("Error creating QFPay session:", error);
        },
      }
    );
  };

  return (
    <>
      <Modal
        isCentered
        size="xl"
        isOpen={isPaymentsOpen}
        onClose={handleClosePayments}
      >
        <ModalOverlay />
        <ModalContent minH="250px" borderRadius="12px">
          <Flex
            color="black"
            h="100%"
            justifyContent="center"
            align="center"
            p={3}
            direction="column"
          >
            <Text mb="10px" fontWeight={600} fontSize="18px">
              Choose your payment method
            </Text>
            <Flex align="center" justify="center" flexDirection={{base:"column", md: "row"}} >
              <Image
                maxW="325px"
                w="65%"
                mt="10px"
                mb="10px"
                src={poweredByStripe.src}
                alt="Powered By Stripe"
              />
            </Flex>

            {/* Stripe Button with Transaction Fee Label */}
            <Box position="relative" w="100%">
              <Box
                position="absolute"
                top="39%"
                left="1%"
                fontSize="xs"
                zIndex="1"
                color="white"
                fontWeight={"600"}
              >
                +$1 fee
              </Box>
              <Button
                w="100%"
                bg="#4FB688"
                _hover={{ opacity: 0.9 }}
                _active={{ opacity: 0.8 }}
                onClick={handleStripeCheckout}
                size="lg"
                mt="25px"
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                  alignItems="center"
                >
                  <GridItem display={{ base: "none", md: "block" }}></GridItem>
                  <Text color="white" fontSize={{ base: "md", md: "lg" }}>
                    Checkout with Stripe
                  </Text>
                  <Image
                    ml={{ base: "10px", md: "20px" }}
                    src={creditCard.src}
                    alt="Credit Card"
                  />
                </Grid>
              </Button>
            </Box>

             {/* QFPay Button with Credit Card and Apple Pay */}
             <Box position="relative" w="100%">
              <Box
                position="absolute"
                top="39%"
                left="1%"
                fontSize="xs"
                zIndex="1"
                color="white"
                fontWeight={"600"}
              >
                +$1 fee
              </Box>
              <Button
                w="100%"
                bg="#0056B3" 
                _hover={{ opacity: 0.9 }}
                _active={{ opacity: 0.8 }}
                onClick={handleQFPayCheckout}
                isLoading={createQFPayCheckoutSession.isPending}
                size="lg"
                mt="25px"
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                  alignItems="center"
                  justifyItems={{base:"center", md: ""}}
                >
                  <GridItem display={{ base: "none", md: "block" }}></GridItem>
                  <Text color="white" fontSize={{ base: "md", md: "lg" }}>
                    Pay with Credit Card
                  </Text>
                  <Flex alignItems="center">
                    <Image
                      ml={{ base: "10px", md: "20px" }}
                      src={kesspayCard.src}
                      alt="Credit Card"
                      h="22px"
                      mr={1}
                    />
                    <Image
                      src={applePayLogo.src}
                      alt="Apple Pay"
                      h="22px"
                    />
                  </Flex>
                </Grid>
              </Button>
            </Box>

            {/* KessPay Button */}
            <Box position="relative" w="100%">
              <Box
                position="absolute"
                top="30%"
                left="1%"
                fontSize="xs"
                zIndex="1"
                color="white"
                fontWeight={"600"}
              >
                +$1 fee
              </Box>
              <Button
                w="100%"
                bg="#5A2FBD" 
                _hover={{ opacity: 0.9 }}
                _active={{ opacity: 0.8 }}
                onClick={handleKessPayCheckout}
                isLoading={createKeypassCheckoutSession.isPending}
                size="lg"
                mb="25px"
                mt="25px"
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                  alignItems="center"
                  justifyItems={{base:"center", md: ""}}
                >
                  <GridItem display={{ base: "none", md: "block" }}></GridItem>
                  <Text color="white" fontSize={{ base: "md", md: "lg" }}>
                    Checkout with KessPay
                  </Text>
                  <Image
                    ml={{ base: "10px", md: "20px" }}
                    src={kesspayCard.src}
                    alt="Credit Card"
                    h="22px"
                  />
                </Grid>
              </Button>
            </Box>

           

            <CryptoPaymentModal plan={plan} price={price} />
          </Flex>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PaymentsModal;